import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Select } from 'src/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { useCreatePulseMutation, useGetPulseSourcesQuery } from './pulse.endpoints';
import { pulseSurveyPath } from './pulse.helpers';
import {
  PULSE_BACKFILL_OPTIONS,
  PULSE_CADENCE_OPTIONS,
  PULSE_GEOGRAPHY_OPTIONS,
  PULSE_VERTICAL_OPTIONS,
  PulseCadence,
  PulseEnabledSources,
  PulseGeographyScope,
  PulseVertical,
} from './pulse.types';

type SourceDraft = {
  enabled: boolean;
  weight: string;
  params: string;
};

const PulseCreateTab = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const { data: sources = [], isLoading: sourcesLoading } = useGetPulseSourcesQuery();
  const [createPulse, { isLoading: creating }] = useCreatePulseMutation();

  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<PulseVertical>('local-approvals');
  const [questionText, setQuestionText] = useState('');
  const [constructDefinition, setConstructDefinition] = useState('');
  const [geographyScope, setGeographyScope] = useState<PulseGeographyScope>('nation');
  const [cadence, setCadence] = useState<PulseCadence>('weekly');
  const [backfillMonths, setBackfillMonths] = useState('12');
  const [sourceDrafts, setSourceDrafts] = useState<Record<string, SourceDraft>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sources.length) return;
    setSourceDrafts((prev) => {
      const next = { ...prev };
      sources.forEach((source, index) => {
        if (!next[source.source_key]) {
          next[source.source_key] = {
            enabled: index < 2,
            weight: '1.00',
            params: '{}',
          };
        }
      });
      return next;
    });
  }, [sources]);

  const enabledCount = useMemo(
    () => Object.values(sourceDrafts).filter((draft) => draft.enabled).length,
    [sourceDrafts],
  );

  const updateDraft = (sourceKey: string, patch: Partial<SourceDraft>) => {
    setSourceDrafts((prev) => ({
      ...prev,
      [sourceKey]: { ...prev[sourceKey], ...patch },
    }));
  };

  const handleSubmit = async () => {
    setError(null);
    if (!name.trim() || !questionText.trim() || !constructDefinition.trim()) {
      setError('Name, question text, and construct definition are required.');
      return;
    }
    if (enabledCount === 0) {
      setError('Enable at least one source.');
      return;
    }

    const enabled_sources: PulseEnabledSources = {};
    for (const source of sources) {
      const draft = sourceDrafts[source.source_key];
      if (!draft?.enabled) continue;
      let params: Record<string, unknown> = {};
      try {
        params = draft.params.trim() ? JSON.parse(draft.params) : {};
      } catch {
        setError(`Invalid JSON params for ${source.source_key}.`);
        return;
      }
      const weight = Number(draft.weight);
      enabled_sources[source.source_key] = {
        enabled: true,
        weight: Number.isFinite(weight) ? weight : 1,
        params,
      };
    }

    try {
      const created = await createPulse({
        name: name.trim(),
        vertical,
        question_text: questionText.trim(),
        construct_definition: constructDefinition.trim(),
        geography_scope: geographyScope,
        cadence,
        enabled_sources,
        backfill_months: Number(backfillMonths) || 0,
      }).unwrap();
      navigate(pulseSurveyPath('pulse-detail', clientId, `id=${created.id}`));
    } catch (err: unknown) {
      const message =
        (err as { data?: { status?: { status_message?: string } } })?.data?.status
          ?.status_message || 'Unable to create pulse.';
      setError(typeof message === 'string' ? message : 'Unable to create pulse.');
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <button
          type="button"
          className="text-sm text-gray-500 hover:text-gray-800"
          onClick={() => navigate(pulseSurveyPath('pulse', clientId))}
        >
          ← Pulses
        </button>
        <h1 className="mt-2 text-2xl font-semibold text-gray-900">New pulse</h1>
      </div>

      <section className="space-y-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">1. Issue</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Name</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Vertical</span>
            <Select
              className="w-full"
              value={vertical}
              onValueChange={(value) => setVertical(value as PulseVertical)}
              options={PULSE_VERTICAL_OPTIONS}
            />
          </label>
        </div>
      </section>

      <section className="space-y-4 rounded-lg border border-amber-100 bg-amber-50/40 p-4">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            2. The question
          </h2>
          <span className="text-xs text-red-600">Freezes on save as construct v1</span>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="text-gray-600">Question text — exactly as it will be published</span>
          <textarea
            className="min-h-20 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
            value={questionText}
            onChange={(event) => setQuestionText(event.target.value)}
          />
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-gray-600">Construct definition — what counts as support</span>
          <textarea
            className="min-h-28 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
            value={constructDefinition}
            onChange={(event) => setConstructDefinition(event.target.value)}
          />
        </label>
        <p className="text-xs text-gray-500">
          Changing either field later mints construct v2 and starts a new trend line. The old series
          stays, labelled with its version.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          3. Measurement
        </h2>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Geography scope</span>
            <Select
              className="w-full"
              value={geographyScope}
              onValueChange={(value) => setGeographyScope(value as PulseGeographyScope)}
              options={PULSE_GEOGRAPHY_OPTIONS}
            />
            <span className="text-xs text-red-600">Immutable after create</span>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Cadence</span>
            <Select
              className="w-full"
              value={cadence}
              onValueChange={(value) => setCadence(value as PulseCadence)}
              options={PULSE_CADENCE_OPTIONS}
            />
            <span className="text-xs text-gray-500">Daily needs a bigger noise floor</span>
          </label>
          <label className="space-y-1 text-sm">
            <span className="text-gray-600">Backfill on create</span>
            <Select
              className="w-full"
              value={backfillMonths}
              onValueChange={setBackfillMonths}
              options={PULSE_BACKFILL_OPTIONS.map((opt) => ({
                value: String(opt.value),
                label: opt.label,
              }))}
            />
            <span className="text-xs text-gray-500">Queued behind live runs</span>
          </label>
        </div>
      </section>

      <section className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">4. Sources</h2>
          <span className="text-xs text-gray-500">
            Weight scales a source&apos;s contribution. Fit rating is set per source after the first
            run.
          </span>
        </div>
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50 hover:bg-gray-50">
                <TableHead className="w-16">On</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Pillars</TableHead>
                <TableHead>Backfill</TableHead>
                <TableHead>Weight</TableHead>
                <TableHead>Params (JSON)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sourcesLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-gray-500">
                    Loading sources…
                  </TableCell>
                </TableRow>
              )}
              {sources.map((source) => {
                const draft = sourceDrafts[source.source_key] || {
                  enabled: false,
                  weight: '1.00',
                  params: '{}',
                };
                return (
                  <TableRow key={source.source_key}>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={draft.enabled}
                        onChange={(event) =>
                          updateDraft(source.source_key, { enabled: event.target.checked })
                        }
                      />
                    </TableCell>
                    <TableCell className="font-medium">{source.source_key}</TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {source.pillars.join(' · ')}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {source.supports_backfill ? 'Supported' : 'None'}
                    </TableCell>
                    <TableCell>
                      <Input
                        className="w-20"
                        value={draft.weight}
                        disabled={!draft.enabled}
                        onChange={(event) =>
                          updateDraft(source.source_key, { weight: event.target.value })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={draft.params}
                        disabled={!draft.enabled}
                        onChange={(event) =>
                          updateDraft(source.source_key, { params: event.target.value })
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </section>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">
          {enabledCount} source{enabledCount === 1 ? '' : 's'} enabled
          {Number(backfillMonths) > 0
            ? ` · backfill ${backfillMonths} months queued on create`
            : ''}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="med"
            onClick={() => navigate(pulseSurveyPath('pulse', clientId))}
          >
            Cancel
          </Button>
          <Button variant="black" size="med" disabled={creating} onClick={handleSubmit}>
            {creating ? 'Creating…' : 'Create and start backfill'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PulseCreateTab;
